import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useT } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// ONE CARD THAT CANNOT RENDER DEGRADES ONLY ITSELF (#1337).
//
// The member home renders records from club data written over several eras.
// Before this, nothing in the web app caught a render error, so ONE malformed
// activity in «Kommende» blanked the whole player app — the member saw an
// empty page and had nothing to tap.
//
// The fix for the record that revealed it is in the projection and the label
// (`memberData.relativeDayLabel`, `MemberActivity.when`). This is the second
// line: whatever the next unanticipated shape is, the card that trusts it is
// replaced by a short notice in place, and the greeting, the level, the two
// actions and every other card still render.
//
// `resetKey` lets a card recover when its record changes (a refresh brings a
// corrected row) without remounting the list.
// ─────────────────────────────────────────────────────────────────────

function SectionFailed() {
  const t = useT();
  return (
    <div
      className="rounded-2xl px-4 py-3 text-[12px] text-ink-3"
      style={{ background: '#fff' }}
      data-testid="mobile-section-failed"
    >
      {t('mobile.common.sectionFailed')}
    </div>
  );
}

export class SectionBoundary extends Component<
  { children: ReactNode; resetKey?: unknown },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[member home] a section could not render', error, info.componentStack);
  }

  override componentDidUpdate(prev: { resetKey?: unknown }) {
    // A NEW record gets a fresh try; the same record stays the notice.
    if (this.state.failed && prev.resetKey !== this.props.resetKey)
      this.setState({ failed: false });
  }

  override render() {
    return this.state.failed ? <SectionFailed /> : this.props.children;
  }
}
